import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { profileStore } from '../stores/profileStore'
import { leaderboardStore } from '../stores/leaderboardStore'
import { gamificationStore } from '../stores/gamificationStore'
import { mojilandStore } from '../stores/mojilandStore'
import { evojiLevels, getEvojiValue } from '../utils/levels'
import { resources } from '../utils/sets'
import { playSound, vibrate } from '../utils/sounds'
import GameLeaderboard from '../components/GameLeaderboard'

const GRID_SIZE = 4

interface Tile {
  id: number
  value: number  // Índice en la secuencia evolutiva (0-10)
  x: number
  y: number
  mergedFrom?: number[]
  isNew?: boolean
}

type Direction = 'up' | 'down' | 'left' | 'right'
type GameStatus = 'playing' | 'won' | 'lost'

// Colores para cada nivel/tema
const LEVEL_COLORS = [
  { bg: 'from-orange-400 to-red-500', border: 'border-orange-400' },      // Camping
  { bg: 'from-green-400 to-emerald-500', border: 'border-green-400' },    // Naturaleza
  { bg: 'from-blue-400 to-cyan-500', border: 'border-blue-400' },         // Ocean
  { bg: 'from-amber-400 to-yellow-500', border: 'border-amber-400' },     // Comida
  { bg: 'from-pink-400 to-rose-500', border: 'border-pink-400' },         // Love
  { bg: 'from-purple-400 to-violet-500', border: 'border-purple-400' },   // Space
  { bg: 'from-cyan-400 to-teal-500', border: 'border-cyan-400' },         // Tech
  { bg: 'from-red-400 to-orange-500', border: 'border-red-400' },         // Fire
]

const Evoji = observer(() => {
  const [level, setLevel] = useState(0)
  const [tiles, setTiles] = useState<Tile[]>([])
  const [score, setScore] = useState(0)
  const [bestScore, setBestScore] = useState(0)
  const [gameStatus, setGameStatus] = useState<GameStatus>('playing')
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [resourcesEarned, setResourcesEarned] = useState<Record<string, number>>({})
  const [keepPlaying, setKeepPlaying] = useState(false)
  const [highestTile, setHighestTile] = useState(0)
  const [showSwipeHint, setShowSwipeHint] = useState(() => {
    return localStorage.getItem('evoji_swipe_hint_seen') !== 'true'
  })
  
  const tileIdCounter = useRef(0)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const isSwiping = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastSavedScore = useRef(0)
  const lastSavedTile = useRef(0)

  const currentLevel = evojiLevels[level] || evojiLevels[0]
  const sequence = currentLevel.sequence

  const sound = useCallback((type: Parameters<typeof playSound>[0]) => {
    if (soundEnabled) playSound(type)
  }, [soundEnabled])

  // Guardar score automáticamente cada 200 puntos o cuando se alcanza un tile alto
  const saveScoreIfNeeded = useCallback((currentScore: number, currentHighestTile: number) => {
    const scoreThreshold = 200 // Guardar cada 200 puntos
    const tileThreshold = 5 // Guardar cuando se alcanza tile nivel 5+ (64 en 2048)
    
    const scoreMilestone = Math.floor(currentScore / scoreThreshold)
    const lastScoreMilestone = Math.floor(lastSavedScore.current / scoreThreshold)
    
    const shouldSaveByScore = scoreMilestone > lastScoreMilestone && currentScore >= 200
    const shouldSaveByTile = currentHighestTile >= tileThreshold && currentHighestTile > lastSavedTile.current
    
    if (shouldSaveByScore || shouldSaveByTile) {
      // Solo guardar si el score actual es mayor al último guardado
      if (currentScore > lastSavedScore.current) {
        leaderboardStore.submitGameScore('evoji', currentScore, level)
        lastSavedScore.current = currentScore
        lastSavedTile.current = Math.max(lastSavedTile.current, currentHighestTile)
        console.log(`[Evoji] Auto-saved score: ${currentScore} (tile: ${currentHighestTile})`)
      }
    }
  }, [level])

  // Generar nuevo ID único para tiles
  const getNewTileId = () => {
    tileIdCounter.current += 1
    return tileIdCounter.current
  }

  // Inicializar juego
  const initGame = useCallback(() => {
    tileIdCounter.current = 0
    lastSavedScore.current = 0 // Reset saved score tracking
    lastSavedTile.current = 0
    const newTiles: Tile[] = []
    
    // Añadir 2 tiles iniciales
    for (let i = 0; i < 2; i++) {
      const emptyPositions = getEmptyPositions(newTiles)
      if (emptyPositions.length > 0) {
        const pos = emptyPositions[Math.floor(Math.random() * emptyPositions.length)]
        newTiles.push({
          id: getNewTileId(),
          value: Math.random() < 0.9 ? 0 : 1, // 90% nivel 0, 10% nivel 1
          x: pos.x,
          y: pos.y,
          isNew: true
        })
      }
    }
    
    setTiles(newTiles)
    setScore(0)
    setGameStatus('playing')
    setResourcesEarned({})
    setKeepPlaying(false)
    setHighestTile(Math.max(...newTiles.map(t => t.value), 0))
    sound('go')
  }, [sound])

  // Obtener posiciones vacías
  const getEmptyPositions = (currentTiles: Tile[]) => {
    const positions: { x: number; y: number }[] = []
    for (let x = 0; x < GRID_SIZE; x++) {
      for (let y = 0; y < GRID_SIZE; y++) {
        if (!currentTiles.some(t => t.x === x && t.y === y)) {
          positions.push({ x, y })
        }
      }
    }
    return positions
  }

  // Añadir tile aleatorio
  const addRandomTile = useCallback((currentTiles: Tile[]): Tile[] => {
    const emptyPositions = getEmptyPositions(currentTiles)
    if (emptyPositions.length === 0) return currentTiles

    const pos = emptyPositions[Math.floor(Math.random() * emptyPositions.length)]
    const resetTiles: Tile[] = currentTiles.map(t => ({ ...t, isNew: false, mergedFrom: undefined as number[] | undefined }))
    return [
      ...resetTiles,
      {
        id: getNewTileId(),
        value: Math.random() < 0.9 ? 0 : 1,
        x: pos.x,
        y: pos.y,
        isNew: true
      }
    ]
  }, [])

  // Verificar si hay movimientos posibles
  const hasAvailableMoves = useCallback((currentTiles: Tile[]): boolean => {
    // Si hay espacios vacíos, hay movimientos
    if (getEmptyPositions(currentTiles).length > 0) return true

    // Verificar si hay tiles adyacentes que puedan fusionarse
    for (const tile of currentTiles) {
      const neighbors = [
        { x: tile.x - 1, y: tile.y },
        { x: tile.x + 1, y: tile.y },
        { x: tile.x, y: tile.y - 1 },
        { x: tile.x, y: tile.y + 1 }
      ]
      for (const neighbor of neighbors) {
        const adjacentTile = currentTiles.find(t => t.x === neighbor.x && t.y === neighbor.y)
        if (adjacentTile && adjacentTile.value === tile.value && tile.value < sequence.length - 1) {
          return true
        }
      }
    }
    return false
  }, [sequence.length])

  // Mover tiles en una dirección
  const move = useCallback((direction: Direction) => {
    if (gameStatus !== 'playing' && !keepPlaying) return

    let moved = false
    let newScore = score
    let newTiles: Tile[] = tiles.map(t => ({ ...t, mergedFrom: undefined, isNew: false }))
    let maxValue = highestTile

    // Determinar orden de procesamiento basado en dirección
    const getTraversalOrder = () => {
      const order: { x: number; y: number }[] = []
      for (let x = 0; x < GRID_SIZE; x++) {
        for (let y = 0; y < GRID_SIZE; y++) {
          order.push({ x, y })
        }
      }

      if (direction === 'right') {
        order.sort((a, b) => b.x - a.x)
      } else if (direction === 'down') {
        order.sort((a, b) => b.y - a.y)
      } else if (direction === 'left') {
        order.sort((a, b) => a.x - b.x)
      } else {
        order.sort((a, b) => a.y - b.y)
      }
      return order
    }

    const getDelta = (): { dx: number; dy: number } => {
      switch (direction) {
        case 'up': return { dx: 0, dy: -1 }
        case 'down': return { dx: 0, dy: 1 }
        case 'left': return { dx: -1, dy: 0 }
        case 'right': return { dx: 1, dy: 0 }
      }
    }

    const delta = getDelta()
    const traversal = getTraversalOrder()
    const mergedPositions = new Set<string>()

    for (const pos of traversal) {
      const tileIndex = newTiles.findIndex(t => t.x === pos.x && t.y === pos.y)
      if (tileIndex === -1) continue

      const tile = newTiles[tileIndex]
      let newX = tile.x
      let newY = tile.y

      // Mover hasta encontrar obstáculo
      while (true) {
        const nextX = newX + delta.dx
        const nextY = newY + delta.dy

        // Fuera del tablero
        if (nextX < 0 || nextX >= GRID_SIZE || nextY < 0 || nextY >= GRID_SIZE) break

        const blockingTile = newTiles.find(t => t.x === nextX && t.y === nextY && t.id !== tile.id)
        
        if (!blockingTile) {
          // Espacio vacío, seguir moviendo
          newX = nextX
          newY = nextY
        } else if (
          blockingTile.value === tile.value && 
          tile.value < sequence.length - 1 &&
          !mergedPositions.has(`${nextX},${nextY}`)
        ) {
          // Fusionar tiles
          newX = nextX
          newY = nextY
          const newValue = tile.value + 1
          const points = getEvojiValue(newValue)
          newScore += points
          maxValue = Math.max(maxValue, newValue)

          // Marcar como fusionado
          mergedPositions.add(`${newX},${newY}`)

          // Eliminar tile bloqueante y actualizar tile actual
          newTiles = newTiles.filter(t => t.id !== blockingTile.id)
          newTiles[newTiles.findIndex(t => t.id === tile.id)] = {
            ...tile,
            x: newX,
            y: newY,
            value: newValue,
            mergedFrom: [tile.id, blockingTile.id]
          }

          moved = true

          // Sonido de fusión
          if (newValue >= 8) {
            sound('superCombo')
            vibrate([50, 30, 50, 30, 100])
          } else if (newValue >= 5) {
            sound('combo')
            vibrate([30, 20, 30])
          } else {
            sound('match')
            vibrate(30)
          }
          break
        } else {
          break
        }
      }

      if (newX !== tile.x || newY !== tile.y) {
        moved = true
        const idx = newTiles.findIndex(t => t.id === tile.id)
        if (idx !== -1) {
          newTiles[idx] = { ...newTiles[idx], x: newX, y: newY }
        }
      }
    }

    if (moved) {
      sound('whoosh')
      newTiles = addRandomTile(newTiles)
      setTiles(newTiles)
      setScore(newScore)
      setHighestTile(maxValue)

      // Auto-guardar score cada 200 puntos o tile alto
      saveScoreIfNeeded(newScore, maxValue)

      // Verificar victoria (alcanzó el último emoji)
      if (maxValue >= sequence.length - 1 && !keepPlaying && gameStatus !== 'won') {
        setGameStatus('won')
        
        // Calcular recursos ganados
        const earned: Record<string, number> = {}
        const resourceReward = Math.floor(newScore / 100) + level + 3
        resources.forEach(r => {
          earned[r] = Math.floor(resourceReward / resources.length) + (Math.random() > 0.5 ? 1 : 0)
        })
        setResourcesEarned(earned)
        
        Object.entries(earned).forEach(([icon, amount]) => {
          profileStore.addResource(icon, amount)
        })
        
        leaderboardStore.submitGameScore('evoji', newScore, level)
        gamificationStore.recordGameResult('evoji', newScore, level, true)
        mojilandStore.tryCompleteFromGame('evoji', newScore, level, true)
        sound('win')
        vibrate([100, 50, 100, 50, 200])
      }

      // Verificar derrota
      if (!hasAvailableMoves(newTiles)) {
        setGameStatus('lost')
        gamificationStore.recordGameResult('evoji', newScore, level, false)
        mojilandStore.tryCompleteFromGame('evoji', newScore, level, false)
        sound('lose')
        vibrate([200, 100, 200])
      }
    } else {
      sound('wrong')
    }
  }, [tiles, score, gameStatus, keepPlaying, highestTile, sequence.length, addRandomTile, hasAvailableMoves, sound, level, saveScoreIfNeeded])

  // Manejar teclas
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault()
        const direction = e.key.replace('Arrow', '').toLowerCase() as Direction
        move(direction)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [move])

  // Manejar gestos táctiles con bloqueo de scroll
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    }
    isSwiping.current = false
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return
    
    const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x)
    const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y)
    
    // Si el movimiento es significativo, bloquear scroll
    if (deltaX > 10 || deltaY > 10) {
      isSwiping.current = true
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return

    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y
    const minSwipe = 30 // Reducido para mejor respuesta

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (Math.abs(deltaX) > minSwipe) {
        move(deltaX > 0 ? 'right' : 'left')
        hideSwipeHint()
      }
    } else {
      if (Math.abs(deltaY) > minSwipe) {
        move(deltaY > 0 ? 'down' : 'up')
        hideSwipeHint()
      }
    }

    touchStartRef.current = null
    isSwiping.current = false
  }

  const hideSwipeHint = () => {
    if (showSwipeHint) {
      setShowSwipeHint(false)
      localStorage.setItem('evoji_swipe_hint_seen', 'true')
    }
  }

  // Iniciar juego al cargar
  useEffect(() => {
    initGame()
  }, [level])

  // Actualizar mejor puntuación
  useEffect(() => {
    if (score > bestScore) {
      setBestScore(score)
    }
  }, [score, bestScore])

  const handleLevelSelect = (newLevel: number) => {
    sound('click')
    setLevel(newLevel)
  }

  const handleKeepPlaying = () => {
    setKeepPlaying(true)
    setGameStatus('playing')
    sound('levelUp')
  }

  // Obtener color de fondo basado en el valor del tile
  const getTileStyle = (value: number) => {
    const colors = [
      'from-slate-200 to-slate-300',      // 0 - 2
      'from-amber-200 to-amber-300',      // 1 - 4
      'from-orange-300 to-orange-400',    // 2 - 8
      'from-orange-400 to-red-400',       // 3 - 16
      'from-red-400 to-red-500',          // 4 - 32
      'from-red-500 to-pink-500',         // 5 - 64
      'from-pink-500 to-purple-500',      // 6 - 128
      'from-purple-500 to-violet-500',    // 7 - 256
      'from-violet-500 to-indigo-500',    // 8 - 512
      'from-indigo-500 to-blue-500',      // 9 - 1024
      'from-yellow-400 to-yellow-500',    // 10 - 2048 (WINNER!)
    ]
    return colors[Math.min(value, colors.length - 1)]
  }

  const getTextSize = (value: number) => {
    if (value >= 8) return 'text-3xl sm:text-4xl'
    return 'text-4xl sm:text-5xl'
  }

  // Bloquear pull-to-refresh en el área del juego
  useEffect(() => {
    const gameArea = containerRef.current
    if (!gameArea) return

    const preventPullToRefresh = (e: TouchEvent) => {
      if (isSwiping.current) {
        e.preventDefault()
      }
    }

    // Prevenir el overscroll/bounce en iOS
    document.body.style.overscrollBehavior = 'none'
    gameArea.addEventListener('touchmove', preventPullToRefresh, { passive: false })

    return () => {
      document.body.style.overscrollBehavior = ''
      gameArea.removeEventListener('touchmove', preventPullToRefresh)
    }
  }, [])

  return (
    <div className="min-h-screen evoji-bg flex flex-col overflow-hidden">
      {/* Header - más compacto en mobile */}
      <header className="sticky top-0 z-40 game-header safe-area-top">
        <div className="max-w-md mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link 
                to="/"
                className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center hover:bg-white/80 text-gray-700 active:scale-95 transition-transform"
              >
                <span className="text-base">←</span>
              </Link>
              <span className="text-xl">{currentLevel.emoji}</span>
              <span className="font-title text-sm text-gray-800">{currentLevel.name}</span>
            </div>
            
            <div className="flex items-center gap-1.5">
              <Link to="/leaderboard" className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center hover:bg-white/80 active:scale-95 transition-transform">
                <span className="text-lg">🏆</span>
              </Link>
              <button 
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center hover:bg-white/80 active:scale-95 transition-transform"
              >
                <span className="text-lg">{soundEnabled ? '🔊' : '🔇'}</span>
              </button>
            </div>
          </div>
          
          {/* Score display - más compacto */}
          <div className="flex justify-center items-center gap-2 mt-2">
            <div className="px-3 py-1.5 rounded-xl bg-white/60 text-sm font-bold border border-white/70 flex items-center gap-1.5">
              <span className="text-gray-500 text-[10px]">SCORE</span>
              <span className="text-gray-800">{score}</span>
            </div>
            <div className="px-3 py-1.5 rounded-xl bg-white/60 text-sm font-bold border border-white/70 flex items-center gap-1.5">
              <span className="text-gray-500 text-[10px]">BEST</span>
              <span className="text-gray-800">{bestScore}</span>
            </div>
            <div className="px-2.5 py-1.5 rounded-xl bg-white/60 text-sm font-bold border border-white/70">
              <span className="text-xl">{sequence[highestTile]}</span>
            </div>
            <button
              onClick={initGame}
              className="px-3 py-1.5 bg-pink-500/90 hover:bg-pink-500 text-white rounded-xl text-sm font-medium transition-colors active:scale-95"
            >
              🔄
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-md mx-auto px-3 py-2 w-full flex flex-col overflow-hidden">
        {/* Evolution sequence preview - horizontal scroll con colores de tiles */}
        <div className="glass-card p-3 mb-2">
          <div className="flex items-center gap-2 overflow-x-auto py-2 px-2 scrollbar-hide">
            {sequence.map((emoji, idx) => (
              <div 
                key={idx}
                className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-lg shadow-md transition-all
                  bg-gradient-to-br ${getTileStyle(idx)}
                  ${idx === highestTile ? 'ring-[3px] ring-white scale-110 shadow-lg z-10' : ''}
                  ${idx > highestTile ? 'opacity-40 grayscale' : ''}
                  ${idx < highestTile ? 'ring-2 ring-green-400/50' : ''}
                `}
              >
                {emoji}
              </div>
            ))}
          </div>
        </div>

        {/* Game board - área principal de juego con touch bloqueado */}
        <div 
          ref={containerRef}
          className="glass-card p-2 mb-2 flex-1 flex items-center justify-center touch-none select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="w-full aspect-square max-w-[340px]">
            <div className="relative w-full h-full bg-white/30 rounded-xl p-2 border border-white/50">
              {/* Grid background */}
              <div className="grid grid-cols-4 gap-2 w-full h-full">
                {Array.from({ length: 16 }).map((_, i) => (
                  <div 
                    key={i} 
                    className="bg-white/40 rounded-lg"
                  />
                ))}
              </div>
              
              {/* Tiles */}
              <div className="absolute inset-2">
                {tiles.map((tile) => {
                  const cellSize = 100 / GRID_SIZE
                  const gap = 0.5 // rem equivalent in %
                  
                  return (
                    <div
                      key={tile.id}
                      className={`
                        absolute rounded-lg flex items-center justify-center
                        bg-gradient-to-br ${getTileStyle(tile.value)}
                        transition-all duration-100 ease-out
                        ${tile.isNew ? 'animate-pop-in' : ''}
                        ${tile.mergedFrom ? 'animate-merge' : ''}
                        shadow-lg
                      `}
                      style={{
                        width: `calc(${cellSize}% - 0.5rem)`,
                        height: `calc(${cellSize}% - 0.5rem)`,
                        left: `calc(${tile.x * cellSize}% + 0.25rem)`,
                        top: `calc(${tile.y * cellSize}% + 0.25rem)`,
                      }}
                    >
                      <span className={`${getTextSize(tile.value)} select-none drop-shadow-md`}>
                        {sequence[tile.value]}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Controls hint - desaparece después del primer swipe */}
        {showSwipeHint && (
          <div className="glass-card p-2 mb-2 animate-pulse">
            <p className="text-center text-gray-600 text-xs font-medium">
              👆 Swipe to merge emojis
            </p>
          </div>
        )}

        {/* Level selector - con colores y scroll horizontal */}
        <div className="glass-card p-3 mb-2">
          <p className="text-center text-gray-500 text-[10px] mb-2 uppercase tracking-wider">Select Theme</p>
          <div className="flex gap-2 overflow-x-auto pb-2 px-1 scrollbar-hide snap-x snap-mandatory">
            {evojiLevels.map((lvl, idx) => {
              const isActive = idx === level
              const colorStyle = LEVEL_COLORS[idx % LEVEL_COLORS.length]
              return (
                <button
                  key={idx}
                  onClick={() => handleLevelSelect(idx)}
                  className={`
                    flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-0.5
                    transition-all duration-200 cursor-pointer border-2 snap-center
                    ${isActive 
                      ? `bg-gradient-to-br ${colorStyle.bg} scale-105 shadow-lg border-white/80 ring-2 ring-white/50` 
                      : `bg-white/50 ${colorStyle.border} hover:bg-white/70 active:scale-95`
                    }
                  `}
                  title={lvl.name}
                >
                  <span className="text-xl">{lvl.emoji}</span>
                  <span className={`text-[8px] font-medium truncate max-w-full px-1 ${isActive ? 'text-white' : 'text-gray-600'}`}>
                    {lvl.name.split(' ')[0]}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Mini Leaderboard - Live score */}
        <div className="mt-2">
          <GameLeaderboard 
            game="evoji"
            gameName="Evoji"
            emoji="🧬"
            currentScore={score}
            gradient="from-pink-500 to-rose-500"
            limit={5}
          />
        </div>

        {/* Win modal */}
        {gameStatus === 'won' && !keepPlaying && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-6xl block mb-3">{sequence[sequence.length - 1]}</span>
              <h2 className="font-title text-2xl text-pink-600 mb-1">Evolution Complete!</h2>
              <p className="text-gray-500 text-sm mb-4">You reached {currentLevel.name}!</p>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-pink-500">{score}</p>
                  <p className="text-xs text-gray-500">Score</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-purple-500">{getEvojiValue(highestTile)}</p>
                  <p className="text-xs text-gray-500">Highest</p>
                </div>
              </div>
              
              {/* Resources */}
              {Object.keys(resourcesEarned).length > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-2">Resources earned:</p>
                  <div className="flex justify-center gap-3">
                    {Object.entries(resourcesEarned).map(([icon, amount]) => (
                      <div key={icon} className="flex items-center gap-1">
                        <span className="text-xl">{icon}</span>
                        <span className="font-bold text-pink-600">+{amount}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 justify-center">
                <button
                  onClick={initGame}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200"
                >
                  🔄 New Game
                </button>
                <button
                  onClick={handleKeepPlaying}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold"
                >
                  Keep Playing →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Game Over modal */}
        {gameStatus === 'lost' && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full text-center shadow-2xl animate-bounce-in">
              <span className="text-6xl block mb-3">😵</span>
              <h2 className="font-title text-2xl text-gray-600 mb-1">Game Over</h2>
              <p className="text-gray-500 text-sm mb-4">No more moves available</p>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 my-4">
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl font-bold text-gray-600">{score}</p>
                  <p className="text-xs text-gray-500">Final Score</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-2">
                  <p className="text-2xl">{sequence[highestTile]}</p>
                  <p className="text-xs text-gray-500">Best Evolution</p>
                </div>
              </div>
              
              <div className="flex gap-2 justify-center">
                <button
                  onClick={initGame}
                  className="px-6 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold"
                >
                  🔄 Try Again
                </button>
                <Link
                  to="/leaderboard"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-semibold"
                >
                  🏆
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default Evoji
